import { getTenantBasePath, resolveTenantFromUrl } from './tenant-resolver';

/** Canonical lobby path: /tenant/{tenant}/lobby/{lobbySlug} */
export function getLobbyHomePath(): string {
  const tenantPath = getTenantBasePath();
  const tenant = resolveTenantFromUrl();
  return `${tenantPath}/lobby/${tenant}`;
}

/** Full lobby home URL for a given origin (e.g. https://freetoplay-prod.low6.com/tenant/dk/lobby/dk). */
export function buildLobbyHomeUrl(origin: string): string {
  const normalized = origin.replace(/\/$/, '');
  return `${normalized}${getLobbyHomePath()}`;
}

const LOBBY_RETURN_PARAM = 'lobbyReturn';

/**
 * Resolve where "Back to Lobby" should navigate.
 *
 * Priority:
 * 1. `lobbyReturn` query param (full URL set by the lobby when launching a game)
 * 2. sessionStorage value captured on first land
 * 3. document.referrer when it points at a /lobby/ route
 * 4. Default lobby home URL using referrer origin or current origin
 */
export function resolveLobbyReturnUrl(
  searchParams: URLSearchParams,
  sessionStorageKey: string
): string {
  const fromParam = searchParams.get(LOBBY_RETURN_PARAM);
  if (fromParam) {
    try {
      return decodeURIComponent(fromParam);
    } catch {
      // ignore malformed value
    }
  }

  const stored = sessionStorage.getItem(sessionStorageKey);
  if (stored) return stored;

  const referrer = document.referrer;
  if (referrer) {
    try {
      const url = new URL(referrer);
      if (url.pathname.includes('/lobby/')) {
        return referrer;
      }
      if (url.origin !== window.location.origin) {
        return buildLobbyHomeUrl(url.origin);
      }
    } catch {
      // ignore malformed referrer
    }
  }

  return buildLobbyHomeUrl(window.location.origin);
}

/** Append lobby return URL to a game play link (used by the lobby app). */
export function appendLobbyReturnToPlayUrl(playUrl: string): string {
  const sep = playUrl.includes('?') ? '&' : '?';
  const lobbyReturn = encodeURIComponent(window.location.href);
  return `${playUrl}${sep}from=lobby&lobbyReturn=${lobbyReturn}`;
}
